/**
 * The one way this portal talks to the API.
 *
 * Every path, parameter, and response shape here is checked at compile time
 * against clients/portal_api/schema.ts, which openapi-typescript generates from
 * api/healtrics-openapi.yaml. CLAUDE.md forbids a hand-written API call, and
 * that rule holds itself up here: a call to a path the contract does not
 * describe does not compile.
 *
 * The bearer token is attached in `authenticatedFetch` below, and a 401 is
 * retried once after a refresh. One refresh runs at a time: five screens
 * loading together would otherwise send five refreshes, and the server rotates
 * the token on each use, so four of them replay a spent token. A replay revokes
 * the whole family and signs the doctor out.
 *
 * The active-clinic header is not injected. The contract makes it required on
 * every /portal path, so the generated types refuse a call that leaves it out.
 * `clinicHeader()` below fills it in from the session, and a call site that
 * forgets it does not compile. Injecting it quietly would have meant casting
 * that check away everywhere.
 */

import createClient from "openapi-fetch";

import type { paths } from "@contract";
import {
  endSession,
  getAccessToken,
  getActiveClinic,
  getRefreshToken,
  replaceTokens,
} from "./session";

// The contract's servers block already carries /v1, so the generated paths do
// not repeat it.
//
// In development this stays relative and the Vite dev server proxies it, so the
// browser sees one origin. A deployed portal is served from its own hostname
// and the API from another, so the build puts the API's origin in front of it.
// The value is baked in at build time, which is why the deploy step has to know
// the environment it is building for.
const BASE_URL = `${import.meta.env.VITE_API_ORIGIN ?? ""}/v1`;

/**
 * The active-clinic header every /portal call needs (ADR-008).
 *
 * It is a request, never a grant. The server checks it against the caller's
 * assignments and reads their role from the one it finds.
 */
export function clinicHeader(): { "X-Active-Clinic": string } {
  const clinic = getActiveClinic();
  if (!clinic) {
    // Reachable only from a screen the router shows to a signed-in session, so
    // this means the guard in App.tsx was bypassed rather than that a doctor
    // did something wrong.
    throw new Error("This call needs an active clinic, and the session has none.");
  }
  return { "X-Active-Clinic": clinic.id };
}

let refreshInFlight: Promise<boolean> | null = null;

/** Swap the refresh token for a new pair. Returns false when it is spent. */
async function refresh(): Promise<boolean> {
  const token = getRefreshToken();
  if (!token) return false;

  const response = await fetch(`${BASE_URL}/auth/refresh`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ refresh_token: token }),
  });

  if (!response.ok) {
    // A refused refresh means the token expired, or it was already used. Either
    // way this session is over, and the server has revoked the family already.
    endSession();
    return false;
  }

  replaceTokens((await response.json()) as { access_token: string; refresh_token: string });
  return true;
}

/** One refresh at a time, however many callers ask for it at once. */
function refreshOnce(): Promise<boolean> {
  if (!refreshInFlight) {
    refreshInFlight = refresh().finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}

/**
 * Bring back a session that a page reload emptied.
 *
 * The access token lives in memory, so a reload loses it. The refresh token
 * outlives the reload, and this trades it for a new pair. Without this a
 * doctor who reloads the page has to find their phone and type a code again.
 *
 * It returns false when there is nothing to restore, which is the ordinary
 * case of a first visit.
 */
export function restoreSession(): Promise<boolean> {
  if (getAccessToken() || !getRefreshToken()) {
    return Promise.resolve(Boolean(getAccessToken()));
  }
  return refreshOnce();
}

function authorize(request: Request): Request {
  const token = getAccessToken();
  if (token) {
    request.headers.set("Authorization", `Bearer ${token}`);
  }
  return request;
}

const authenticatedFetch: typeof fetch = async (input, init) => {
  const request = new Request(input, init);
  // A body can only be read once, so the retry needs its own copy. Taking it
  // before the first send is the only moment the body is still there.
  const retryable = request.clone();

  const response = await fetch(authorize(request));
  if (response.status !== 401) {
    return response;
  }

  if (!(await refreshOnce())) {
    return response;
  }
  return fetch(authorize(retryable));
};

export const api = createClient<paths>({ baseUrl: BASE_URL, fetch: authenticatedFetch });

/**
 * The error envelope every failure carries (DSN-HLT-008 section 2).
 *
 * `details` is what makes a refusal useful. A 403 on a chart names the ways
 * forward, so this portal draws the break-glass button from the reply rather
 * than from its own copy of the access rules.
 */
export interface ApiError {
  code: string;
  message: string;
  details?: Record<string, unknown>;
}

const UNREACHABLE: ApiError = {
  code: "unreachable",
  message: "The server did not answer. Check the connection and try again.",
};

/** Read an error out of whatever a failed call returned. */
export function readError(error: unknown): ApiError {
  if (
    typeof error === "object" &&
    error !== null &&
    "code" in error &&
    typeof (error as ApiError).code === "string"
  ) {
    return error as ApiError;
  }
  return UNREACHABLE;
}

/** Whether a refusal offers break-glass as a way forward (CLN-F-040). */
export function breakGlassOffered(error: ApiError): boolean {
  const options = error.details?.["options"];
  return Array.isArray(options) && options.includes("break_glass");
}
